import { useEffect, useMemo } from 'react';
import { GoodsDto, SiteDto } from '../api/ClientApi';
import { importAllGoods } from '../redux/actions/goodsActions';
import { RootState } from '../redux/reducers';
import { useAppDispatch, useAppSelector } from './hooks';

// Sites from the store with `goods` filled from the goods slice.
//
// /api/Site/all no longer carries goods (the same units, with article and batch, come from
// /api/Goods/all, which the orders page loads anyway), so anything that reads `site.goods` for
// per-site stock goes through here. While goods are still loading `site.goods` is undefined,
// which the availability helpers already treat as "unknown, no limit".
export function useSitesWithGoods(): SiteDto[] | null {
    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const goods = useAppSelector((state: RootState) => state.goods.goods);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    return useMemo(() => {
        if (!sites) {
            return null;
        }

        const goodsBySite = new Map<number, GoodsDto[]>();

        goods?.forEach((g) => {
            if (g.siteId === undefined) {
                return;
            }

            const forSite = goodsBySite.get(g.siteId);

            if (forSite) {
                forSite.push(g);
            } else {
                goodsBySite.set(g.siteId, [g]);
            }
        });

        return sites.map((site) => {
            // A real SiteDto instance (not a spread copy): the generated client calls toJSON() on
            // nested DTOs when a form sends the site back.
            const withGoods = new SiteDto(site);

            withGoods.goods = goods ? (goodsBySite.get(site.id ?? -1) ?? []) : undefined;

            return withGoods;
        });
    }, [sites, goods]);
}
